import React, { useRef, useState } from 'react';
import { useSnackbar } from 'notistack';

import { PHOTO_ACCEPT, photoFileProblem } from '../../utils/personPhoto';
import { personPhotoService, PhotoOwner } from '../../services/personPhotoService';

/**
 * Upload / remove a person's photo, for any component that offers it: opens
 * the file picker, checks the file, saves it (the server deletes the photo
 * it replaces) and reports back. Render `fileInput` once in the component.
 */
export function usePersonPhotoActions(owner: PhotoOwner | undefined, onChanged?: (photoFilename: string | null) => void) {
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const save = async (work: () => Promise<string | null>, done: string): Promise<boolean> => {
    setBusy(true);
    try {
      onChanged?.(await work());
      enqueueSnackbar(done, { variant: 'success' });
      return true;
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not update the photo', { variant: 'error' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const upload = (file?: File) => {
    if (!file || !owner) return;
    const problem = photoFileProblem(file);
    if (problem) { enqueueSnackbar(problem, { variant: 'error' }); return; }
    save(() => personPhotoService.upload(owner, file), 'Photo updated');
  };

  return {
    busy,
    /** Open the file picker; the chosen photo saves straight away. */
    choose: () => inputRef.current?.click(),
    remove: () => (owner
      ? save(async () => { await personPhotoService.remove(owner); return null; }, 'Photo removed')
      : Promise.resolve(false)),
    fileInput: (
      <input
        ref={inputRef}
        type="file"
        accept={PHOTO_ACCEPT}
        hidden
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }}
      />
    ),
  };
}
